import React, { useState } from 'react';
import { fetchUserCv, hireApplicant, rejectApplicant, shortlistApplicant } from '../../../services/api';
import { getMediaUrl } from '../../../utils/mediaUrl';
import { getCallLink } from '../../../utils/phoneLinks';
import CardIcon from '../../../shared/components/icons/CardIcon';

const statusStyles = {
  pending: 'bg-orange-100 text-orange-700 border-orange-200',
  hired: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  shortlisted: 'bg-blue-100 text-blue-700 border-blue-200',
  rejected: 'bg-rose-100 text-rose-700 border-rose-200',
};

function formatDate(date) {
  if (!date) return 'Unknown date';
  const dateText = String(date).split(',')[0].trim();
  const slashDate = dateText.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const parsed = slashDate
    ? new Date(Number(slashDate[3]), Number(slashDate[2]) - 1, Number(slashDate[1]))
    : new Date(date);
  return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function getApplicantName(applicant) {
  return `${applicant?.firstName || ''} ${applicant?.lastName || ''}`.trim() || 'Unknown applicant';
}

function getReferenceWhatsAppLink(phone, applicantName, experienceTitle, jobTitle) {
  const cleanNumber = phone.replace(/\D/g, '').startsWith('260')
    ? phone.replace(/\D/g, '')
    : `260${phone.replace(/\D/g, '')}`;
  const message = `Hello, I am reviewing ${applicantName} for the ${jobTitle} position on Tuma Jobs. ${applicantName} listed you as an employer reference for ${experienceTitle}. Could you please confirm the work completed, their reliability, quality of work, and whether you would recommend them for this job? Thank you.`;
  return `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
}

function formatCvDate(date) {
  return date ? formatDate(date) : 'Date unavailable';
}

function formatExperienceDateTime(date) {
  if (!date) return 'Date unavailable';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;

  const pad = (value) => String(value).padStart(2, '0');
  return `${pad(parsed.getDate())}.${pad(parsed.getMonth() + 1)}.${parsed.getFullYear()} ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

function normalizeCvResponse(response) {
  let value = response;
  while (value?.data && !value.profile && !value.experience && !value.skills) {
    value = value.data;
  }
  return value || {};
}

function getSkillYears(skill) {
  const value = Number(skill?.yearsOfExperience ?? skill?.experienceYears ?? skill?.years ?? skill?.experience ?? 0);
  return Number.isFinite(value) ? value : 0;
}

function getReferenceList(cv, person) {
  if (Array.isArray(cv?.references)) {
    return cv.references.filter(Boolean);
  }

  if (Array.isArray(cv?.referenceList)) {
    return cv.referenceList.filter(Boolean);
  }

  if (cv?.references && typeof cv.references === 'string') {
    return [cv.references];
  }

  if (person?.references && Array.isArray(person.references)) {
    return person.references.filter(Boolean);
  }

  return [];
}

function ConfirmationModal({ action, applicantName, onCancel, onConfirm, loading }) {
  const copy = {
    rejected: {
      title: 'Reject this applicant?',
      message: `${applicantName} will be notified that their application was not selected.`,
      button: 'Reject application',
      color: 'rose',
    },
    shortlisted: {
      title: 'Shortlist this applicant?',
      message: `${applicantName} will move to your shortlist for further review.`,
      button: 'Shortlist applicant',
      color: 'orange',
    },
    hired: {
      title: 'Hire this applicant?',
      message: `${applicantName} will be marked as hired for this job.`,
      button: 'Hire applicant',
      color: 'emerald',
    },
  }[action];
  const colorClasses = {
    rose: 'bg-rose-600 hover:bg-rose-700',
    orange: 'bg-orange hover:opacity-90',
    emerald: 'bg-emerald-600 hover:bg-emerald-700',
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <section className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="application-action-title">
        <div className="grid h-12 w-12 place-items-center rounded-full bg-slate-100 text-xl">?</div>
        <h2 id="application-action-title" className="mt-4 text-xl font-bold text-navy">{copy.title}</h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">{copy.message}</p>
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onCancel} disabled={loading} className="rounded-2xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">Cancel</button>
          <button type="button" onClick={onConfirm} disabled={loading} className={`rounded-2xl px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60 ${colorClasses[copy.color]}`}>
            {loading ? 'Updating...' : copy.button}
          </button>
        </div>
      </section>
    </div>
  );
}

function ActionResponseModal({ response, onClose }) {
  const isSuccess = response.type === 'success';

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <section className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl" role="alertdialog" aria-modal="true" aria-labelledby="application-response-title">
        <div className={`grid h-12 w-12 place-items-center rounded-full text-xl font-bold ${isSuccess ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
          {isSuccess ? '✓' : '!'}
        </div>
        <h2 id="application-response-title" className="mt-4 text-xl font-bold text-navy">{isSuccess ? 'Action completed' : 'Action could not be completed'}</h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">{response.message}</p>
        <div className="mt-6 flex justify-end">
          <button type="button" onClick={onClose} className="rounded-2xl bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90">Done</button>
        </div>
      </section>
    </div>
  );
}

export function CvModal({ cv, applicant, job, onClose, hideShare = false }) {
  const person = { ...(applicant?.applicant || {}), ...(cv?.user || {}) };
  const name = getApplicantName(person);
  const profile = cv?.profile || person?.profile || {};
  const phone = cv?.phone || cv?.user?.phone || person?.phone || profile.phone || '';
  const location = [cv?.area || cv?.user?.area || person?.area, cv?.city || cv?.user?.city || person?.city].filter(Boolean).join(', ');
  const skills = cv?.skills || person?.skills || [];
  const experience = cv?.experience || cv?.workHistory || [];
  const rating = cv?.reputationScore ?? cv?.overallRating ?? profile.overallRating;
  const photo = getMediaUrl(cv?.profilePhoto || profile.profilePhoto || person.profilePhoto || person.profile?.profilePhoto);
  const initials = `${person?.firstName?.[0] || ''}${person?.lastName?.[0] || ''}`.toUpperCase() || 'U';
  const [showAllExperience, setShowAllExperience] = useState(false);
  const visibleExperience = showAllExperience ? experience : experience.slice(0, 3);
  const referenceItems = getReferenceList(cv, person);

  const handleShareCv = () => {
    const printRoot = document.getElementById('cv-modal-root');
    if (!printRoot) {
      window.print();
      return;
    }

    const printWindow = window.open('', '_blank', 'width=900,height=1200');
    if (!printWindow) {
      window.print();
      return;
    }

    const doc = printWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><title>${name} CV</title><style>body{font-family:Arial,sans-serif;padding:24px;color:#111827;background:#fff}*{box-sizing:border-box}h1,h2,h3,p{margin:0 0 8px}section{margin-bottom:20px;border-top:1px solid #e2e8f0;padding-top:12px}.header{display:flex;gap:16px;align-items:center;margin-bottom:20px}.avatar{width:72px;height:72px;border-radius:18px;display:flex;align-items:center;justify-content:center;background:#fed7aa;color:#7c2d12;font-weight:700;font-size:24px;overflow:hidden}.meta{font-size:13px;line-height:1.7}.badge{color:#16a34a;font-weight:700}.section-title{font-size:11px;text-transform:uppercase;letter-spacing:0.16em;color:#f97316;font-weight:700}.skill{margin:0 0 14px}.skill-name{font-weight:700}.small{font-size:13px;color:#475569}.experience{border:1px solid #e2e8f0;padding:12px;border-radius:12px;margin-bottom:12px}.actions{display:flex;gap:8px;flex-wrap:wrap;margin-top:10px}.action{padding:6px 10px;border:1px solid #d1d5db;border-radius:9px;font-size:12px;background:#f8fafc}.rating{font-weight:700;color:#f97316;font-size:24px}.@media print{body{padding:0}}</style></head><body>`);
    doc.write(`
      <div class="header">
        <div class="avatar">${photo ? `<img src="${photo}" style="width:100%;height:100%;object-fit:cover" />` : initials}</div>
        <div>
          <h1>${name}</h1>
          <p class="small">${cv?.headline || cv?.profession || job?.title || 'Applicant profile'}</p>
          <div class="meta">
            <div>${location || 'Location unavailable'}</div>
            <div>${phone || 'Phone not provided'}</div>
            <div class="badge">${person.isWhatsapp || cv?.user?.isWhatsapp || cv?.isWhatsapp ? 'WhatsApp Available' : 'Contact available'}</div>
          </div>
        </div>
      </div>
      <section><div class="section-title">Personal information</div><div class="meta"><div>Sex: ${profile.gender || profile.sex || cv?.gender || 'Not provided'}</div><div>Date of birth: ${profile.dateOfBirth ? formatCvDate(profile.dateOfBirth) : 'Not provided'}</div><div>NRC: ${profile.nationalId || cv?.nationalId || cv?.nrc || 'Not provided'}</div><div>Availability: ${(profile.availability || cv?.availability || 'Not provided').replace('_', ' ')}</div></div></section>
      <section><div class="section-title">Professional summary</div><p>${(profile.bio || cv?.summary || cv?.bio || 'No professional summary provided.').replace(/</g, '&lt;')}</p></section>
      <section><div class="section-title">Skills & Services</div>${skills.length ? skills.map((skill) => `<div class="skill"><div class="skill-name">★ ${skill.name || skill.title || skill?.skillName || 'Skill'}</div><div class="small">${getSkillYears(skill)} year${getSkillYears(skill) === 1 ? '' : 's'} experience</div><div class="small">${skill.certificateName || skill.certificate || skill.certification || 'No certificate'}</div></div>`).join('') : '<div class="small">No skills listed.</div>'}</section>
      <section><div class="section-title">Tuma Jobs Work History</div>${experience.length ? visibleExperience.map((item) => { const experienceTitle = item.title || item.position || item.jobTitle || 'Work experience'; const description = item.description || item.details || 'No description provided.'; const employerName = item.employerName || item.employer?.name || item.employer?.firstName || 'Employer'; const ratingValue = item.rating ?? item.score ?? item.reviewRating; return `<div class="experience"><h3>${experienceTitle}</h3><p>${description}</p><div class="small">${formatExperienceDateTime(item.startDate || item.date || item.completedAt)}${ratingValue !== null && ratingValue !== undefined ? `  •  ★ ${ratingValue}/5` : ''}</div><div class="small">Employer: ${employerName}</div><div class="actions"><span class="action">Call</span><span class="action">Text</span><span class="action">WhatsApp</span></div></div>`; }).join('') : '<div class="small">No work experience listed.</div>'}</section>
      <section><div class="section-title">Rating</div><div class="rating">★ ${rating ? rating : 'Not rated'} / 5</div></section>
      <section><div class="section-title">References</div>${referenceItems.length ? referenceItems.map((reference) => { const ref = typeof reference === 'string' ? { name: reference } : reference; const nameLabel = ref.name || ref.fullName || ref.person || 'Reference'; return `<div class="experience"><div class="skill-name">${nameLabel}</div><div class="actions"><span class="action">Call</span><span class="action">Text</span><span class="action">WhatsApp</span></div></div>`; }).join('') : '<div class="small">References available upon request.</div>'}</section>
    `);
    doc.write('</body></html>');
    doc.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };

  return (
    <div id="cv-modal-root" className="fixed inset-0 z-[60] overflow-y-auto bg-slate-950/50 p-4 backdrop-blur-sm sm:p-8">
      <section className="mx-auto max-w-3xl overflow-hidden rounded-3xl bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="cv-title">
        <header className="bg-navy px-6 py-6 text-white sm:px-10">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-2xl bg-orange-100 text-2xl font-bold text-orange-700">{photo ? <img src={photo} alt={name} className="h-full w-full object-cover" /> : initials}</div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-orange-300">Curriculum vitae</p>
                <h2 id="cv-title" className="mt-2 text-2xl font-bold sm:text-3xl">{name}</h2>
                <p className="mt-1 text-sm text-white/75">{cv?.headline || cv?.profession || applicant?.applicant?.occupation || 'Applicant profile'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!hideShare && <button type="button" onClick={handleShareCv} className="rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white hover:bg-white/15">Share CV</button>}
              <button type="button" onClick={onClose} aria-label="Close CV" className="rounded-xl p-2 text-2xl leading-none text-white/70 hover:bg-white/10">&times;</button>
            </div>
          </div>
          <div className="mt-5 space-y-1 text-sm text-slate-200">
            <p>{location || 'Location unavailable'}</p>
            <p>{phone || 'Phone not provided'}</p>
            <p className="font-semibold text-emerald-300">{person.isWhatsapp || cv?.user?.isWhatsapp || cv?.isWhatsapp ? 'WhatsApp Available' : 'Contact available'}</p>
          </div>
        </header>

        <div className="space-y-7 p-6 sm:p-10">
          <CvSection title="Personal information">
            <div className="grid gap-3 text-sm sm:grid-cols-2">
              <p><span className="text-slate-500">Sex:</span> <strong>{profile.gender || profile.sex || cv?.gender || 'Not provided'}</strong></p>
              <p><span className="text-slate-500">Date of birth:</span> <strong>{profile.dateOfBirth ? formatCvDate(profile.dateOfBirth) : 'Not provided'}</strong></p>
              <p><span className="text-slate-500">NRC:</span> <strong>{profile.nationalId || cv?.nationalId || cv?.nrc || 'Not provided'}</strong></p>
              <p><span className="text-slate-500">Availability:</span> <strong>{(profile.availability || cv?.availability || 'Not provided').replace('_', ' ')}</strong></p>
            </div>
          </CvSection>

          <CvSection title="Professional summary">
            <p className="text-sm leading-7 text-slate-700">{profile.bio || cv?.summary || cv?.bio || 'No professional summary provided.'}</p>
          </CvSection>

          <CvSection title="Skills & Services">
            <div className="space-y-4">
              {skills.length ? skills.map((skill, index) => {
                const skillName = skill.name || skill.title || skill?.skillName || 'Skill';
                const years = getSkillYears(skill);
                const certificateName = skill.certificateName || skill.certificate || skill.certification || '';

                return (
                  <div key={skill.id || skillName || index} className="space-y-1">
                    <p className="flex items-center gap-2 text-sm font-bold text-slate-900">
                      <span className="text-orange-600">★</span>
                      {skillName}
                    </p>
                    <p className="text-sm text-slate-600">{years} year{years === 1 ? '' : 's'} experience</p>
                    <p className="inline-flex items-center gap-1.5 text-sm text-slate-600">{certificateName ? <><CardIcon name="graduation" />{certificateName}</> : 'No certificate'}</p>
                  </div>
                );
              }) : <p className="text-sm text-slate-600">No skills listed.</p>}
            </div>
          </CvSection>

          <CvSection title="Tuma Jobs Work History">
            <div className="space-y-4">
              {experience.length ? visibleExperience.map((item, index) => {
                const employerPhone = item.employerPhone || item.employerWhatsapp || item.employer?.phone;
                const experienceTitle = item.title || item.position || item.jobTitle || 'Work experience';
                const employerName = item.employerName || item.employer?.name || item.employer?.firstName || 'Employer';
                const description = item.description || item.details || 'No description provided.';
                const ratingValue = item.rating ?? item.score ?? item.reviewRating;

                return (
                  <div key={item.id || index} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-lg font-bold text-navy">{experienceTitle}</p>
                    <p className="mt-2 text-sm leading-6 text-slate-700">{description}</p>
                    <div className="mt-3 flex items-center justify-between gap-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <span>{formatExperienceDateTime(item.startDate || item.date || item.completedAt)}</span>
                      {ratingValue !== null && ratingValue !== undefined && <span className="text-orange-600">★ {ratingValue}/5</span>}
                    </div>
                    {(item.employerName || item.employer || item.employerPhone) && (
                      <p className="mt-3 text-sm text-slate-600">Employer: <strong>{employerName}</strong></p>
                    )}
                    <div className="mt-4 flex flex-wrap gap-2">
                      {employerPhone && <a href={getCallLink(employerPhone)} className="rounded-xl bg-navy px-3 py-2 text-xs font-semibold text-white">Call</a>}
                      {employerPhone && <a href={`sms:${employerPhone}`} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">Text</a>}
                      {employerPhone && <a href={getReferenceWhatsAppLink(employerPhone, name, experienceTitle, job?.title || 'this job')} target="_blank" rel="noreferrer" className="rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">WhatsApp</a>}
                    </div>
                  </div>
                );
              }) : <p className="text-sm text-slate-600">No work experience listed.</p>}

              {experience.length > 3 && (
                <div className="pt-2 text-center">
                  <button type="button" onClick={() => setShowAllExperience((current) => !current)} className="text-sm font-semibold text-orange-700 hover:text-orange-800">
                    {showAllExperience ? 'Show less experience' : `View More Experience →`}
                  </button>
                  {showAllExperience && <p className="mt-2 text-xs uppercase tracking-wide text-slate-500">Showing {experience.length} of {experience.length} experiences</p>}
                </div>
              )}
            </div>
          </CvSection>

          <CvSection title="Rating">
            <p className="text-lg font-bold text-orange-600">{rating ? `★ ${rating} / 5` : 'Not rated'}</p>
          </CvSection>

          <CvSection title="References">
            {referenceItems.length ? (
              <div className="space-y-5">
                {referenceItems.map((reference, index) => {
                  const ref = typeof reference === 'string' ? { name: reference } : reference;
                  const phone = ref.phone || ref.mobile || ref.contact || ref.whatsapp;
                  const nameLabel = ref.name || ref.fullName || ref.person || 'Reference';

                  return (
                    <div key={`${nameLabel}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm font-bold text-slate-900">{nameLabel}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {phone && <a href={getCallLink(phone)} className="rounded-xl bg-navy px-3 py-2 text-xs font-semibold text-white">Call</a>}
                        {phone && <a href={`sms:${phone}`} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">Text</a>}
                        {phone && <a href={`https://wa.me/${String(phone).replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">WhatsApp</a>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-700">References available upon request.</p>
            )}
          </CvSection>
        </div>
      </section>
    </div>
  );
}

function CvSection({ title, children }) {
  return <section className="border-t border-slate-200 pt-5"><h3 className="text-xs font-bold uppercase tracking-[0.16em] text-orange-600">{title}</h3><div className="mt-3">{children}</div></section>;
}

function ApplicantDrawer({ job, applicant, onClose, onStatusChange }) {
  const person = applicant?.applicant;
  const name = getApplicantName(person);
  const location = [person?.area, person?.city].filter(Boolean).join(', ');
  const status = applicant?.status?.toLowerCase() || 'pending';
  const initials = `${person?.firstName?.[0] || ''}${person?.lastName?.[0] || ''}`.toUpperCase();
  const [cv, setCv] = useState(null);
  const [loadingView, setLoadingView] = useState(null);
  const [action, setAction] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionResponse, setActionResponse] = useState(null);
  const [error, setError] = useState('');
  const photo = getMediaUrl(person?.profilePhoto || person?.profile?.profilePhoto);
  const hasPhoto = Boolean(photo);

  const loadView = async () => {
    setError('');
    setLoadingView('cv');
    try {
      const userId = person?.userId
        || person?.id
        || person?.user?.id
        || applicant?.applicantId
        || applicant?.userId
        || applicant?.user?.id;
      if (!userId) throw new Error('Unable to identify this applicant.');
      const data = await fetchUserCv(userId);
      setCv(normalizeCvResponse(data));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoadingView(null);
    }
  };

  const updateStatus = async (status, request) => {
    setError('');
    setAction(status);
    try {
      const result = await request(applicant?.applicationId);
      onStatusChange(result?.status || status);
      setConfirmAction(null);
      setActionResponse({
        type: 'success',
        message: `${name} was ${status === 'shortlisted' ? 'shortlisted' : status === 'hired' ? 'hired' : 'rejected'} successfully.`,
      });
    } catch (requestError) {
      setError(requestError.message);
      setConfirmAction(null);
      setActionResponse({ type: 'error', message: requestError.message || 'Please try again.' });
    } finally {
      setAction(null);
    }
  };

  return (
    <>
      <button type="button" aria-label="Close applicant details" className="fixed inset-0 z-40 bg-slate-950/30" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-xl flex-col overflow-y-auto border-l border-slate-200 bg-white shadow-2xl" aria-label="Applicant details">
        <div className="flex items-center justify-between bg-navy px-6 py-5 text-white">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-white/60">Application review</p>
            <h2 className="mt-1 text-lg font-bold">Applicant Details</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl p-2 text-2xl leading-none text-white/80 hover:bg-white/10" aria-label="Close">&times;</button>
        </div>

        <div className="space-y-7 p-6 sm:p-8">
          <div className="flex items-center gap-4 rounded-3xl border border-slate-200 bg-slate-50 p-4">
            <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-2xl bg-orange-100 text-lg font-bold text-orange-700">
              {hasPhoto ? <img src={photo} alt={name} className="h-full w-full object-cover" /> : initials || 'U'}
            </div>
            <div>
              <h3 className="text-xl font-bold text-navy">{name}</h3>
              <p className="mt-1 text-sm text-slate-600">{location || 'Location unavailable'}</p>
              <span className={`mt-2 inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusStyles[status] || statusStyles.pending}`}>
                {status.toUpperCase()}
              </span>
            </div>
          </div>

          <section className="border-t border-slate-200 pt-5">
            <p className="text-xs font-bold uppercase tracking-widest text-orange-600">Application</p>
            <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-3"><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Position</dt><dd className="mt-1 font-semibold text-navy">{job.skill?.name || 'Unknown'}</dd></div>
              <div className="rounded-2xl border border-slate-200 bg-white p-3"><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Job</dt><dd className="mt-1 font-semibold text-navy">{job.title || 'Untitled job'}</dd></div>
              <div className="rounded-2xl border border-slate-200 bg-white p-3"><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Applied</dt><dd className="mt-1 font-semibold text-navy">{formatDate(applicant?.appliedAt?.date || applicant?.appliedAt?.full)}</dd></div>
            </dl>
            <div className="mt-5">
              <p className="text-sm font-semibold text-slate-700">Proposal</p>
              <p className="mt-2 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700">{applicant?.proposal?.message || 'No proposal message provided.'}</p>
            </div>
          </section>

          <section className="border-t border-slate-200 pt-5">
            <p className="text-xs font-bold uppercase tracking-widest text-orange-600">Applicant CV</p>
            <p className="mt-3 rounded-2xl bg-slate-50 p-4 text-sm leading-6 text-slate-600">Open the applicant&apos;s complete CV to review personal information, skills, work history and reputation.</p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <button type="button" onClick={loadView} disabled={Boolean(loadingView)} className="w-full rounded-2xl bg-navy px-4 py-3 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-60">{loadingView === 'cv' ? 'Loading CV...' : 'View CV'}</button>
            </div>
          </section>

          {error && <p className="rounded-2xl bg-rose-50 p-3 text-sm text-rose-700">{error}</p>}
          <div className="flex flex-col gap-3 border-t border-slate-200 pt-5 sm:flex-row">
            <button type="button" onClick={() => setConfirmAction('shortlisted')} disabled={Boolean(action)} className="flex-1 rounded-2xl bg-orange px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-60">Shortlist</button>
            <button type="button" onClick={() => setConfirmAction('rejected')} disabled={Boolean(action)} className="flex-1 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-2.5 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60">Reject</button>
            <button type="button" onClick={() => setConfirmAction('hired')} disabled={Boolean(action)} className="flex-1 rounded-2xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">Hire</button>
          </div>
        </div>
      </aside>
      {cv && <CvModal cv={cv} applicant={applicant} job={job} onClose={() => setCv(null)} hideShare />}
      {confirmAction && <ConfirmationModal action={confirmAction} applicantName={name} onCancel={() => setConfirmAction(null)} onConfirm={() => updateStatus(confirmAction, confirmAction === 'rejected' ? rejectApplicant : confirmAction === 'shortlisted' ? shortlistApplicant : hireApplicant)} loading={Boolean(action)} />}
      {actionResponse && <ActionResponseModal response={actionResponse} onClose={() => setActionResponse(null)} />}
    </>
  );
}

function ApplicantListModal({ job, applicants, onSelect, onClose }) {
  return (
    <>
      <button type="button" aria-label="Close applicants" className="fixed inset-0 z-40 bg-navy/40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-50 grid place-items-center p-4 sm:p-6">
        <section className="flex max-h-[min(720px,calc(100vh-2rem))] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="applicants-title">
          <header className="flex items-center justify-between bg-navy px-5 py-5 text-white sm:px-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-white/60">Applicants</p>
              <h2 id="applicants-title" className="mt-1 text-xl font-bold">{job.title || 'Untitled job'}</h2>
              <p className="mt-1 text-sm text-white/70">{job.skill?.name || 'Unknown skill'} · {[job.area, job.city].filter(Boolean).join(', ')}</p>
            </div>
            <button type="button" onClick={onClose} className="rounded-xl p-2 text-2xl leading-none text-white/80 hover:bg-white/10" aria-label="Close applicants">&times;</button>
          </header>
          <div className="overflow-y-auto p-4 sm:p-6">
            <div className="mb-4 flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <span className="text-sm font-semibold text-slate-700">Applicants for this job</span>
              <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-bold text-orange-700">{applicants.length}</span>
            </div>
            <div className="space-y-3">
              {applicants.length > 0 ? applicants.map((item) => {
                const name = getApplicantName(item.applicant);
                const location = [item.applicant?.area, item.applicant?.city].filter(Boolean).join(', ');
                const status = item.status?.toLowerCase() || 'pending';
                const initials = `${item.applicant?.firstName?.[0] || ''}${item.applicant?.lastName?.[0] || ''}`.toUpperCase();
                const applicantPhoto = getMediaUrl(item.applicant?.profilePhoto || item.applicant?.profile?.profilePhoto);
                return (
                  <div key={item.applicationId} className="flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-orange-200 hover:shadow-md sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                      <div className="relative grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-full bg-orange-100 font-bold text-orange-700">
                        <span>{initials || 'U'}</span>
                        {applicantPhoto && <img src={applicantPhoto} alt="" className="absolute inset-0 h-full w-full object-cover" onError={(event) => event.currentTarget.remove()} />}
                      </div>
                      <div>
                        <p className="font-bold text-navy">{name}</p>
                        <p className="mt-1 text-sm text-slate-500">{location || 'Location unavailable'}</p>
                        <p className="mt-1 text-xs text-slate-400">Applied {formatDate(item.appliedAt?.date || item.appliedAt?.full)}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-3 sm:flex-col sm:items-end">
                      <span className={`rounded-full border px-3 py-1 text-xs font-bold ${statusStyles[status] || statusStyles.pending}`}>{status.toUpperCase()}</span>
                      <button type="button" onClick={() => onSelect(item)} className="rounded-2xl bg-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90">View Application</button>
                    </div>
                  </div>
                );
              }) : <p className="rounded-2xl bg-slate-50 p-6 text-center text-sm text-slate-500">No applicants yet.</p>}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

function ClientApplicationJobCard({ job }) {
  const [applicantsOpen, setApplicantsOpen] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [applicants, setApplicants] = useState(job.applicants || []);
  const statistics = job.statistics || {};
  const location = [job.city, job.area].filter(Boolean).join(' · ');
  const pendingCount = statistics.pendingCount ?? applicants.filter((item) => item.status?.toLowerCase() === 'pending').length;
  const hiredCount = statistics.hiredCount ?? applicants.filter((item) => item.status?.toLowerCase() === 'hired').length;

  const handleStatusChange = (applicationId, status) => {
    setApplicants((currentApplicants) => currentApplicants.map((item) => (
      item.applicationId === applicationId ? { ...item, status } : item
    )));
    setSelectedApplicant((currentApplicant) => currentApplicant
      ? { ...currentApplicant, status }
      : currentApplicant);
  };

  return (
    <>
      <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md sm:p-6">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-navy">{job.title || 'Untitled job'}</h2>
            <p className="mt-1 font-semibold text-slate-700">{job.skill?.name || 'Unknown skill'}</p>
            <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-slate-600"><CardIcon name="location" />{location || 'Unavailable'}</p>
            <p className="mt-1 text-sm text-slate-500">Posted {formatDate(job.postedAt?.date || job.postedAt?.full)}</p>
          </div>
          <div className="flex shrink-0 flex-col items-start gap-2 text-sm text-slate-700 sm:items-end">
            <span>Applicants: <strong>{statistics.totalApplicants ?? applicants.length}</strong></span>
            {pendingCount > 0 && <span className="font-semibold text-orange-600">{pendingCount} Pending</span>}
            {hiredCount > 0 && <span className="font-semibold text-emerald-600">{hiredCount} Hired</span>}
            <button type="button" onClick={() => setApplicantsOpen(true)} className="mt-2 rounded-2xl bg-navy px-4 py-2 font-semibold text-white hover:opacity-90">
              View Applicants <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>

      </article>
      {applicantsOpen && <ApplicantListModal job={job} applicants={applicants} onClose={() => setApplicantsOpen(false)} onSelect={(item) => { setSelectedApplicant(item); setApplicantsOpen(false); }} />}
      {selectedApplicant && (
        <ApplicantDrawer
          job={job}
          applicant={selectedApplicant}
          onClose={() => setSelectedApplicant(null)}
          onStatusChange={(status) => handleStatusChange(selectedApplicant.applicationId, status)}
        />
      )}
    </>
  );
}

export default ClientApplicationJobCard;
